import * as Dialog from "@radix-ui/react-dialog";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { saveAs } from "file-saver";
import { QRCodeCanvas } from "qrcode.react";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { BsQrCode } from "react-icons/bs";
import {
  FaFacebookF,
  FaFacebookMessenger,
  FaWhatsapp,
  FaXTwitter,
} from "react-icons/fa6";
import {
  FiArrowLeft,
  FiCheck,
  FiDownload,
  FiLink,
  FiMail,
  FiShare,
  FiUser,
  FiX,
} from "react-icons/fi";
import { MdOutlineAlbum } from "react-icons/md";
import { toast } from "react-toastify";

import {
  getChannelLinks,
  getShareData,
  hasNativeShare,
  isAbortError,
  isTouchDevice,
  type ShareTarget,
} from "@/components/share/share.utils";
import { analytics, EventEnum } from "@/lib/analytics";
import { modalTokens } from "@/lib/tokens";
import { cn } from "@/lib/utils";

export type ShareModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  target: ShareTarget;
};

const QR_EXPORT_SIZE = 512;
const QR_DISPLAY_SIZE = 200;

const iconButtonClass =
  "flex h-10 w-10 items-center justify-center rounded-full text-[#525966] hover:text-brand-primary cursor-pointer transition-colors";

type ShareTileProps = {
  icon: ReactNode;
  label: string;
  ariaLabel: string;
  sublabel?: string;
  href?: string;
  newTab?: boolean;
  onClick?: () => void;
};

function ShareTile({
  icon,
  label,
  ariaLabel,
  sublabel,
  href,
  newTab,
  onClick,
}: ShareTileProps) {
  const className =
    "group flex min-w-0 flex-col items-center gap-1.5 text-center cursor-pointer outline-none";
  const content = (
    <>
      <span className="flex h-12 w-12 items-center justify-center rounded-full bg-blue-100 text-[20px] text-brand-primary transition-colors group-hover:bg-blue-200 group-focus-visible:ring-2 group-focus-visible:ring-brand-primary group-focus-visible:ring-offset-2">
        {icon}
      </span>
      <span className="text-sm font-semibold leading-tight text-text-primary">
        {label}
      </span>
      {sublabel && (
        <span className="-mt-1 text-xs leading-tight text-text-tertiary">
          {sublabel}
        </span>
      )}
    </>
  );

  if (href) {
    return (
      <a
        href={href}
        onClick={onClick}
        aria-label={ariaLabel}
        className={className}
        {...(newTab && { target: "_blank", rel: "noopener noreferrer" })}
      >
        {content}
      </a>
    );
  }

  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={ariaLabel}
      className={className}
    >
      {content}
    </button>
  );
}

function TargetSummary({ target }: { target: ShareTarget }) {
  const isArtist = target.kind === "artist";
  const subtitle = isArtist ? "Artist" : target.artistName;

  return (
    <div className="flex min-w-0 items-center gap-3">
      <div
        className={cn(
          "flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden bg-background-tertiary text-foreground-quarternary",
          isArtist ? "rounded-full" : "rounded-md",
        )}
      >
        {target.artworkUrl ? (
          <img
            src={target.artworkUrl}
            alt=""
            className="h-full w-full object-cover"
          />
        ) : isArtist ? (
          <FiUser className="h-7 w-7" />
        ) : (
          <MdOutlineAlbum className="h-8 w-8" />
        )}
      </div>
      <div className="flex min-w-0 flex-col">
        <span className="truncate font-figtree text-md font-semibold leading-snug text-text-primary">
          {target.title}
        </span>
        {subtitle && (
          <span className="truncate text-base text-text-secondary">
            {subtitle}
          </span>
        )}
      </div>
    </div>
  );
}

function toFileSlug(text: string) {
  return (
    text
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-|-$/g, "") || "lutely"
  );
}

export default function ShareModal({
  open,
  onOpenChange,
  target,
}: ShareModalProps) {
  const [view, setView] = useState<"main" | "qr">("main");
  const [copied, setCopied] = useState(false);
  const copiedTimeoutRef = useRef<number>(0);
  const qrCanvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    if (open) {
      analytics.track(EventEnum.SHARE_MENU_OPENED, {
        url: target.url,
        kind: target.kind,
        surface: "modal",
      });
    }
  }, [open, target.url, target.kind]);

  useEffect(() => () => window.clearTimeout(copiedTimeoutRef.current), []);

  const trackShare = (channel: string) =>
    analytics.track(EventEnum.SHARE_CREATED, {
      url: target.url,
      kind: target.kind,
      channel,
    });

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(target.url);
      setCopied(true);
      window.clearTimeout(copiedTimeoutRef.current);
      copiedTimeoutRef.current = window.setTimeout(
        () => setCopied(false),
        2000,
      );
      trackShare("link");
    } catch {
      toast.error("Failed to copy");
    }
  };

  const handleNativeShare = async () => {
    try {
      await navigator.share(getShareData(target));
      trackShare("native");
    } catch (error) {
      if (!isAbortError(error)) toast.error("Couldn't open the share sheet");
    }
  };

  const handleDownloadQr = () => {
    qrCanvasRef.current?.toBlob((blob) => {
      if (!blob) {
        toast.error("Failed to download QR code");
        return;
      }
      saveAs(blob, `${toFileSlug(target.title)}-qr-code.png`);
      trackShare("qr-download");
    }, "image/png");
  };

  const links = getChannelLinks(target);
  const isTouch = isTouchDevice();

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className={cn(modalTokens.overlay, "z-50")} />
        <Dialog.Content
          aria-describedby={undefined}
          className="fixed left-1/2 top-1/2 z-50 max-h-[calc(100dvh-32px)] w-[calc(100%-32px)] max-w-[380px] -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-lg bg-white p-5 shadow-[0_14px_20px_-1px_rgba(5,12,43,0.14)] focus:outline-none"
          data-testid="share-modal"
        >
          <VisuallyHidden>
            <Dialog.Title>Share {target.title}</Dialog.Title>
          </VisuallyHidden>
          <Dialog.Close
            className={cn(modalTokens.closeButton, "cursor-pointer")}
            aria-label="Close"
          >
            <FiX className="h-5 w-5" />
          </Dialog.Close>

          {view === "main" ? (
            <>
              <div className="pr-10">
                <TargetSummary target={target} />
              </div>
              <div className="mt-6 grid grid-cols-4 gap-x-1 gap-y-4">
                <ShareTile
                  icon={copied ? <FiCheck /> : <FiLink />}
                  label={copied ? "Copied" : "Copy link"}
                  ariaLabel={copied ? "Link copied" : "Copy link"}
                  onClick={handleCopy}
                />
                {hasNativeShare() && (
                  <ShareTile
                    icon={<FiShare />}
                    label="Share…"
                    sublabel="(on mobile)"
                    ariaLabel="Share with your device"
                    onClick={handleNativeShare}
                  />
                )}
                <ShareTile
                  icon={<FaXTwitter />}
                  label="X"
                  ariaLabel="Share on X"
                  href={links.x}
                  newTab
                  onClick={() => trackShare("x")}
                />
                <ShareTile
                  icon={<FaFacebookF />}
                  label="Facebook"
                  ariaLabel="Share on Facebook"
                  href={links.facebook}
                  newTab
                  onClick={() => trackShare("facebook")}
                />
                <ShareTile
                  icon={<FiMail />}
                  label="Email"
                  ariaLabel="Share by email"
                  href={links.email}
                  onClick={() => trackShare("email")}
                />
                {/* Messenger's web dialog needs an FB app id we don't have */}
                {isTouch && (
                  <ShareTile
                    icon={<FaFacebookMessenger />}
                    label="Messenger"
                    ariaLabel="Share on Messenger"
                    href={links.messenger}
                    onClick={() => trackShare("messenger")}
                  />
                )}
                <ShareTile
                  icon={<FaWhatsapp />}
                  label="WhatsApp"
                  ariaLabel="Share on WhatsApp"
                  href={links.whatsapp}
                  newTab
                  onClick={() => trackShare("whatsapp")}
                />
                <ShareTile
                  icon={<BsQrCode />}
                  label="QR code"
                  ariaLabel="Show QR code"
                  onClick={() => setView("qr")}
                />
              </div>
            </>
          ) : (
            <>
              <div className="-ml-2 -mt-2 flex items-center gap-1 pr-10">
                <button
                  type="button"
                  onClick={() => setView("main")}
                  aria-label="Back to share options"
                  className={iconButtonClass}
                  autoFocus
                >
                  <FiArrowLeft className="h-5 w-5" />
                </button>
                <span className="font-figtree text-md font-semibold text-text-primary">
                  QR code
                </span>
              </div>
              <div className="mt-3">
                <TargetSummary target={target} />
              </div>
              <div
                className="mt-4 flex justify-center rounded-lg border border-border-tertiary bg-white p-4"
                data-testid="share-modal-qr"
              >
                <QRCodeCanvas
                  ref={qrCanvasRef}
                  value={target.url}
                  size={QR_EXPORT_SIZE}
                  marginSize={2}
                  style={{ width: QR_DISPLAY_SIZE, height: QR_DISPLAY_SIZE }}
                  role="img"
                  aria-label={`QR code for ${target.title}`}
                />
              </div>
              <button
                type="button"
                onClick={handleDownloadQr}
                className="mt-4 flex h-10 w-full items-center justify-center gap-2 rounded-full bg-brand-primary font-open-sans text-base font-semibold text-white transition-colors hover:bg-blue-700 cursor-pointer"
              >
                <FiDownload className="h-4 w-4" />
                Download PNG
              </button>
            </>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
